import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'

import { inputClass, labelClass, Req } from './index'

interface FieldProps {
  label: ReactNode
  hideLabel?: boolean
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
}

const invalidClass = 'border-red-500 dark:border-red-400'

function useFieldIds(id: string | undefined, hint: ReactNode, error: ReactNode) {
  const auto = useId()
  const base = id ?? auto
  const hintId = hint ? `${base}-hint` : undefined
  const errorId = error ? `${base}-error` : undefined
  return { id: base, describedBy: [errorId, hintId].filter(Boolean).join(' ') || undefined, hintId, errorId }
}

/** Label above, control, then the hint and the error below it: the one form-field layout. */
function FieldShell({
  id,
  label,
  hideLabel,
  hint,
  hintId,
  error,
  errorId,
  required,
  className = '',
  children,
}: FieldProps & { id: string; hintId?: string; errorId?: string; children: ReactNode }) {
  return (
    <div className={`relative text-sm ${className}`}>
      <label htmlFor={id} className={hideLabel ? 'sr-only' : labelClass}>
        {label}
        {required && <Req />}
      </label>
      {children}
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  )
}

type InputProps = FieldProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> & {
  type?: 'text' | 'search' | 'email' | 'number' | 'tel' | 'url' | 'password' | 'date' | 'time' | 'month'
  inputClassName?: string
  /** Sits inside the input's right edge, e.g. a show/hide button. */
  trailing?: ReactNode
}

export const TextInput = forwardRef<HTMLInputElement, InputProps>(function TextInput(
  { label, hideLabel, hint, error, required, className, inputClassName = '', id, type = 'text', trailing, ...rest },
  ref,
) {
  const ids = useFieldIds(id, hint, error)
  const input = (
    <input
      ref={ref}
      id={ids.id}
      type={type}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={ids.describedBy}
      className={`${inputClass} w-full ${error ? invalidClass : ''} ${trailing ? 'pr-10' : ''} ${inputClassName}`}
      {...rest}
    />
  )
  return (
    <FieldShell {...{ label, hideLabel, hint, error, required, className }} {...ids}>
      {trailing ? (
        <div className="relative">
          {input}
          <div className="absolute inset-y-0 right-0 flex items-center pr-1">{trailing}</div>
        </div>
      ) : (
        input
      )}
    </FieldShell>
  )
})

type TextAreaProps = FieldProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & { inputClassName?: string }

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hideLabel, hint, error, required, className, inputClassName = '', id, rows = 3, ...rest },
  ref,
) {
  const ids = useFieldIds(id, hint, error)
  return (
    <FieldShell {...{ label, hideLabel, hint, error, required, className }} {...ids}>
      <textarea
        ref={ref}
        id={ids.id}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        className={`${inputClass} w-full ${error ? invalidClass : ''} ${inputClassName}`}
        {...rest}
      />
    </FieldShell>
  )
})

type SelectProps = FieldProps & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & { inputClassName?: string }

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hideLabel, hint, error, required, className, inputClassName = '', id, children, ...rest },
  ref,
) {
  const ids = useFieldIds(id, hint, error)
  return (
    <FieldShell {...{ label, hideLabel, hint, error, required, className }} {...ids}>
      <select
        ref={ref}
        id={ids.id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        className={`${inputClass} w-full ${error ? invalidClass : ''} ${inputClassName}`}
        {...rest}
      >
        {children}
      </select>
    </FieldShell>
  )
})

/** A tick for a filter or a pick from a list. A setting that takes effect on its own is a Switch, not this. */
export const Checkbox = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'className'> & {
    label: ReactNode
    hideLabel?: boolean
    onChange: (checked: boolean) => void
    className?: string
  }
>(function Checkbox({ label, hideLabel = false, onChange, disabled, className = '', ...rest }, ref) {
  return (
    <label
      className={`inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${className}`}
    >
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        {...rest}
      />
      <span className={hideLabel ? 'sr-only' : undefined}>{label}</span>
    </label>
  )
})
